import DynamicTable from "@components/BaseTable/DynamicTable";
import CustomButton from "@components/common/Button";
import { notify } from "@components/common/Notification";
import { capitalizeText } from "@lib/capitalizeText";
import { downloadCSV } from "@lib/downloadCSV";
import {
  DataOperationsService,
  DynamicModuleService,
  SchemaService,
} from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Dropdown, MenuProps, Popconfirm } from "antd";
import { Edit2, FileText, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const ModulePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const { schemaName } = useParams<{ schemaName: string }>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const {
    data: moduleData,
    isLoading: moduleLoading,
    refetch,
  } = useQuery({
    queryKey: ["moduleData", schemaName, page, pageSize],
    queryFn: () =>
      DynamicModuleService.getGridColItems(schemaName ?? "", page, pageSize),
    enabled: !!schemaName,
  });

  const { data: columnGridData, isLoading: columnGridLoading } = useQuery({
    queryKey: ["columnGridData", schemaName],
    queryFn: () => SchemaService.getColumnGridData(schemaName ?? ""),
  });
  const total = moduleData?.count ?? 0;

  // Mutation hooks
  const handleDelete = async (id: string) => {
    const response = await DynamicModuleService.deleteItem(
      schemaName ?? "",
      id
    );
    if (response) {
      refetch();
    }
  };

  const handleExport = async (request: any) => {
    const data = await DataOperationsService.exportData(
      schemaName ?? "",
      request
    );
    if (data) {
      downloadCSV(data, `${schemaName}-export`);
      setSelectedRowKeys([]);
    }
  };

  const handleBulkDelete = async (ids: string[]) => {
    const response = await DynamicModuleService.bulkDeleteItems(
      schemaName ?? "",
      ids
    );
    if (response) {
      refetch();
      setSelectedRowKeys([]);
    }
  };

  // Dynamic columns generation
  const dynamicColumns: ProColumns<any>[] =
    columnGridData?.grid?.map((field: any) => {
      const getRenderFunction = (fieldType: string) => {
        switch (fieldType) {
          case "switch":
            return (text: any) => (text ? "Yes" : "No");
          default:
            return (text: any) => text ?? "-";
        }
      };

      return {
        title: field.field_title,
        dataIndex: field.field_name.toLowerCase(),
        render: getRenderFunction(field.field_type),
      };
    }) ?? [];

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.MODULE_EDIT(schemaName ?? "", id));
  };

  const handleExportAction = () => {
    if (selectedRowKeys.length === 0) {
      notify.warning(
        "No Selection",
        "Please select at least one item to export."
      );
      return;
    }

    handleExport({
      columns: dynamicColumns.map((column: any) => column.dataIndex),
      ids: selectedRowKeys as string[],
    });
  };

  const handleBulkDeleteAction = () => {
    if (selectedRowKeys.length === 0) {
      notify.warning(
        "No Selection",
        "Please select at least one item to delete."
      );
      return;
    }

    handleBulkDelete(selectedRowKeys as string[]);
  };

  // Handle import navigation
  const handleImportNavigation = () => {
    navigate(navigationRoutes.IMPORT_ADD);
  };

  // Table columns configuration
  const columns: ProColumns<any>[] = [
    ...dynamicColumns,
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record.id)}
          type="text"
          title="Edit Record"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this item?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            title="Delete Record"
          />
        </Popconfirm>,
      ],
    },
  ];

  // Toolbar menu items
  const getMenuItems = (): MenuProps["items"] => {
    if (selectedRowKeys.length > 0) {
      return [
        {
          key: "export",
          label: (
            <span className="flex items-center gap-2 text-sm">
              <FileText size={16} />
              Export CSV
            </span>
          ),
          onClick: handleExportAction,
          disabled: false,
        },
        {
          key: "delete",
          label: (
            <Popconfirm
              title="Are you sure you want to delete this item?"
              onConfirm={handleBulkDeleteAction}
              okText="Yes"
              cancelText="No"
            >
              <span className="flex items-center gap-2 text-sm text-red-500">
                <Trash size={16} />
                Delete
              </span>
            </Popconfirm>
          ),
        },
      ];
    }

    return [
      {
        key: "import",
        label: (
          <span className="flex items-center gap-2 text-sm">
            <FileText size={16} />
            Import
          </span>
        ),
        onClick: handleImportNavigation,
      },
    ];
  };

  // Toolbar render function
  const toolBarRender = () => [
    <CustomButton
      key="add"
      type="primary"
      icon={<Plus size={16} />}
      onClick={() => navigate(navigationRoutes.MODULE_ADD(schemaName ?? ""))}
      title="Add New Record"
    >
      Add New
    </CustomButton>,
    <Dropdown key="actions" menu={{ items: getMenuItems() }}>
      <CustomButton title="More Actions">Actions</CustomButton>
    </Dropdown>,
  ];

  return (
    <DynamicTable<any>
      loading={moduleLoading ?? columnGridLoading}
      columns={columns}
      dataSource={moduleData ?? []}
      page={page}
      pageSize={pageSize}
      total={total}
      selectedRowKeys={selectedRowKeys}
      setSelectedRowKeys={setSelectedRowKeys}
      rowKey="id"
      toolBarRender={toolBarRender}
      headerTitle={`${capitalizeText(schemaName ?? "")} List`}
      onPageChange={(newPage, newSize) => {
        setPage(newPage);
        setPageSize(newSize ?? 10);
        searchParams.set("page", newPage.toString());
        searchParams.set("pageSize", newSize?.toString() ?? "10");
        navigate(
          `${navigationRoutes.MODULE(schemaName ?? "")}?${searchParams.toString()}`
        );
      }}
      request={() => refetch()}
      tableName={schemaName ?? ""}
    />
  );
};

export default ModulePage;
